import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeftIcon, Loader2Icon } from 'lucide-react';
import { generateUUID, sanitizeTcpaHtml, tcpaPost, tcpaSearch, type LeadAnswers, type LeadContext, type TcpaSearchResult } from '../../utils/mediaMatchersApi';
import { injectLeadCertificationScripts, readCertificationFields, waitForTrustedFormCert } from '../../utils/leadCertificationScripts';
import { LeadTrackingFields } from '../LeadTrackingFields';
import { lookupZip, validateZipCode } from '../../utils/addressValidation';
import { trackFormSubmit } from '../../utils/trackFormSubmit';
import { PROGRAM_INFO_PUB_GROUP_ID } from '../../data/programInformationPages';
const EDUCATION_OPTIONS = [{
  value: 'high-school',
  label: 'High School Diploma/GED'
}, {
  value: 'some-high-school',
  label: 'Some High School'
}, {
  value: 'some-college',
  label: 'Some College'
}, {
  value: 'associates',
  label: "Associate's Degree"
}, {
  value: 'bachelors',
  label: "Bachelor's Degree"
}, {
  value: 'masters',
  label: "Master's Degree"
}];
const START_OPTIONS = [{
  value: 'immediately',
  label: 'Immediately'
}, {
  value: '1-3-months',
  label: 'Within 1–3 months'
}, {
  value: '3-6-months',
  label: 'Within 3–6 months'
}, {
  value: '6-12-months',
  label: 'Within 6–12 months'
}, {
  value: '1-year-plus',
  label: 'More than a year out'
}, {
  value: 'undecided',
  label: 'Undecided'
}];
const FALLBACK_CONSENT = 'By checking this box and clicking "See My Matches," I provide my electronic signature and express written consent for findonlinedegrees.org and up to four (4) partner schools to contact me at the phone number and email provided above — including by SMS/text messages and calls using an automatic telephone dialing system or an artificial/prerecorded voice — regarding educational opportunities. Message and data rates may apply. Message frequency varies. Consent is not a condition of enrollment or purchase. I can revoke consent at any time.';
const STEP_LABELS = ['Background', 'Location', 'Contact'];
const fieldClasses = 'w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-gray-900 transition-colors placeholder:text-gray-400 focus:border-[#1e40af] focus:outline-none focus:ring-2 focus:ring-[#1e40af]/25';
const chevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 0.75rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
  paddingRight: '2.5rem'
};
function FieldLabel({
  htmlFor,
  children
}: {
  htmlFor: string;
  children: React.ReactNode;
}) {
  return <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-gray-700" data-id="element-545" data-mp="2sgzpp6pi3wv6">
      {children}
    </label>;
}
const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
interface ProgramMatchFormProps {
  /** Fixed by the page's slug — never asked of the visitor. */
  degreeInterest: number;
  areaStudy: number;
  mProgram: number;
  /** Called once the consent record has been posted. */
  onComplete: (answers: LeadAnswers, context: LeadContext) => void;
}
export function ProgramMatchForm({
  degreeInterest,
  areaStudy,
  mProgram,
  onComplete
}: ProgramMatchFormProps) {
  const location = useLocation();
  const sessionIdRef = useRef<string>('');
  if (!sessionIdRef.current) sessionIdRef.current = generateUUID();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tcpa, setTcpa] = useState<TcpaSearchResult | null>(null);
  const [tcpaLoading, setTcpaLoading] = useState(false);
  const [tcpaError, setTcpaError] = useState('');
  const tcpaRequested = useRef(false);
  const [form, setForm] = useState({
    highestEducation: params.get('highestEducation') || '',
    gradYear: (params.get('gradYear') || '').replace(/\D/g, '').slice(0, 4),
    startTimeframe: params.get('startTimeframe') || '',
    zipCode: (params.get('zipCode') || '').replace(/\D/g, '').slice(0, 5),
    city: '',
    state: '',
    address: (params.get('address') || params.get('streetAddress') || '').trim(),
    firstName: params.get('firstName') || '',
    lastName: params.get('lastName') || '',
    email: params.get('email') || '',
    phone: '',
    consent: false
  });
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({
      length: current - 1989
    }, (_, i) => String(current - i));
  }, []);
  const answers: LeadAnswers = {
    degreeInterest,
    areaStudy,
    mProgram,
    highestEducation: form.highestEducation,
    gradYear: form.gradYear,
    startTimeframe: form.startTimeframe,
    // Not asked on this template — sent as "None" so the field is never blank.
    militaryAffiliation: params.get('military') || 'none',
    zipCode: form.zipCode,
    city: form.city,
    state: form.state,
    address: form.address,
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    phone: form.phone
  };
  const context: LeadContext = {
    search: location.search,
    sessionId: sessionIdRef.current,
    defaultPubGroupID: PROGRAM_INFO_PUB_GROUP_ID
  };

  // Both certification scripts must observe the whole session, so they are
  // injected as soon as the form mounts rather than at submit time.
  useEffect(() => {
    injectLeadCertificationScripts();
  }, []);

  // The disclosure text and mRecordID are fetched when the contact step opens,
  // so the consent the visitor signs is the partner's own current language.
  useEffect(() => {
    if (step !== 3 || tcpaRequested.current) return;
    tcpaRequested.current = true;
    let active = true;
    setTcpaLoading(true);
    setTcpaError('');
    const run = async () => {
      // A prefilled deep link can reach this step before TrustedForm has
      // issued its certificate; don't create the record without one.
      await waitForTrustedFormCert();
      const result = await tcpaSearch(answers, context);
      if (!active) return;
      if (!result) {
        setTcpaError('Unable to load consent terms. Please refresh and try again.');
      } else {
        setTcpa(result);
      }
      setTcpaLoading(false);
    };
    run();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const setField = (key: keyof typeof form, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
    if (error) setError('');
  };
  const handleZipChange = async (raw: string) => {
    const zip = raw.replace(/\D/g, '').slice(0, 5);
    setField('zipCode', zip);
    if (zip.length !== 5) return;
    const place = await lookupZip(zip);
    if (place) {
      setForm(prev => ({
        ...prev,
        city: place.city,
        state: place.state
      }));
    }
  };
  const handleContinue = () => {
    if (step === 1) {
      if (!form.highestEducation) return setError('Please select your highest level of education.');
      if (!form.gradYear) return setError('Please select your high school graduation year.');
      if (!form.startTimeframe) return setError('Please tell us when you want to start.');
      setStep(2);
      return;
    }
    if (step === 2) {
      const zip = validateZipCode(form.zipCode);
      if (!zip.valid) return setError(zip.message);
      setStep(3);
    }
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.firstName.trim()) return setError('Please enter your first name.');
    if (!form.lastName.trim()) return setError('Please enter your last name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return setError('Please enter a valid email address.');
    }
    if (form.phone.replace(/\D/g, '').length !== 10) {
      return setError('Please enter a valid 10-digit phone number.');
    }
    if (!form.consent) {
      return setError('Please check the consent box to continue.');
    }
    setSubmitting(true);
    // The consent record is posted first; reporting waits for whichever
    // transport confirms so a blocked request never counts as a lead.
    tcpaPost(answers, context, tcpa?.mRecordID || '', () => {
      trackFormSubmit({
        formId: 'program-information-request-info',
        pageUrl: window.location.pathname,
        leadId: readCertificationFields().leadIdToken || sessionIdRef.current
      });
    });
    onComplete(answers, context);
  };
  const disclosureHtml = sanitizeTcpaHtml(tcpa?.tcpaDisclaimer || tcpa?.disclaimer || FALLBACK_CONSENT, 'underline text-[#1e40af] hover:text-[#17307d]');
  return <section aria-label="Get matched with schools" className="overflow-hidden rounded-xl bg-white shadow-xl" data-id="element-546" data-mp="27ftimgprrga6">
      <div className="bg-[#1e40af] px-6 py-4 text-center" data-id="element-547" data-mp="1xnnslcj2v8xm">
        <h2 className="text-sm font-bold uppercase tracking-wide text-white" data-id="element-548" data-mp="1xo6zly76wcwv">
          Get Matched Now
        </h2>
        <p className="mt-0.5 text-sm text-blue-100" data-id="element-549" data-mp="2lbf7mnw0602l">
          Accredited programs. No obligation.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5"
    // Steps differ in height; a floor keeps the card from jumping as the
    // visitor advances through them.
    style={{
      minHeight: '22rem'
    }} data-id="element-550" data-mp="2cd3fuijegw8k">
        <LeadTrackingFields data-id="element-551" data-mp="10wvcwg023iwd" />

        <div className="mb-4" data-id="element-552" data-mp="10zr5rpx1rhdy">
          <div className="flex items-baseline justify-between" data-id="element-553" data-mp="14zrlrezso0n4">
            <p className="text-xs font-bold uppercase tracking-wide text-[#1e40af]" data-id="element-554" data-mp="14x5frjsjxt70">
              Step {step} of 3 · {STEP_LABELS[step - 1]}
            </p>
            <span className="text-xs font-semibold text-gray-500" data-id="element-555" data-mp="1jq2jiu71dyb3">
              {Math.round(step / 3 * 100)}%
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={3} aria-label={`Step ${step} of 3`} data-id="element-556" data-mp="15xwdi0xll7pd">
            <div className="h-full rounded-full bg-[#1d4ed8] transition-[width] duration-200 ease-out" style={{
            width: `${step / 3 * 100}%`
          }} data-id="element-557" data-mp="11278vj6q10m7" />
          </div>
        </div>

        {step === 1 && <div className="space-y-4" data-id="element-558" data-mp="2hf5lnrba0dbd">
            <div data-id="element-559" data-mp="2hg79yxhaywb1">
              <FieldLabel htmlFor="pi-education" data-id="element-560" data-mp="2hfnpb909pttb">
                Highest level of education
              </FieldLabel>
              <select id="pi-education" value={form.highestEducation} onChange={e => setField('highestEducation', e.target.value)} className={`${fieldClasses} cursor-pointer appearance-none`} style={chevronStyle} data-id="element-561" data-mp="2le4zy2iwdmfh">
                <option value="" data-id="element-562" data-mp="es7b6rvu0kys">-- Please Select --</option>
                {EDUCATION_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-563" data-mp="tglltozhpdpw">
                    {option.label}
                  </option>)}
              </select>
            </div>
            <div data-id="element-564" data-mp="12hjgvhryaawc">
              <FieldLabel htmlFor="pi-grad-year" data-id="element-565" data-mp="12i1m33wlbzge">
                High school graduation year
              </FieldLabel>
              <select id="pi-grad-year" value={form.gradYear} onChange={e => setField('gradYear', e.target.value)} className={`${fieldClasses} cursor-pointer appearance-none`} style={chevronStyle} data-id="element-566" data-mp="xk04knetc871">
                <option value="" data-id="element-567" data-mp="137ymltpux0fz">-- Please Select --</option>
                {years.map(year => <option key={year} value={year} data-id="element-568" data-mp="186t50ujwwmzk">
                    {year}
                  </option>)}
                <option value="1989" data-id="element-569" data-mp="1d3td1b0bnztb">1989 or earlier</option>
              </select>
            </div>
            <div data-id="element-570" data-mp="1h814vh22tvvr">
              <FieldLabel htmlFor="pi-start" data-id="element-571" data-mp="1h4dbchwmt9h1">
                When would you like to start?
              </FieldLabel>
              <select id="pi-start" value={form.startTimeframe} onChange={e => setField('startTimeframe', e.target.value)} className={`${fieldClasses} cursor-pointer appearance-none`} style={chevronStyle} data-id="element-572" data-mp="1r4mao4lx7b0n">
                <option value="" data-id="element-573" data-mp="24wsovl5ij7xg">-- Please Select --</option>
                {START_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-574" data-mp="29ulw8hijy7zb">
                    {option.label}
                  </option>)}
              </select>
            </div>
          </div>}

        {step === 2 && <div className="space-y-4" data-id="element-575" data-mp="3pd09g5bshwit">
            <div data-id="element-576" data-mp="3lcg9yv3qugti">
              <FieldLabel htmlFor="pi-zip" data-id="element-577" data-mp="3lby7vhueh8do">ZIP code</FieldLabel>
              <input id="pi-zip" inputMode="numeric" autoComplete="postal-code" value={form.zipCode} onChange={e => handleZipChange(e.target.value)} placeholder="90210" className={fieldClasses} data-id="element-578" data-mp="3lekmu2jmeq5x" />
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-579" data-mp="8566rr81scnk">
              <div data-id="element-580" data-mp="83v4jvu93cyk">
                <FieldLabel htmlFor="pi-city" data-id="element-581" data-mp="37d5ncllq8u5">City</FieldLabel>
                <input id="pi-city" autoComplete="address-level2" value={form.city} onChange={e => setField('city', e.target.value)} className={fieldClasses} data-id="element-582" data-mp="350o30jra0hm" />
              </div>
              <div data-id="element-583" data-mp="feye465ovvkp">
                <FieldLabel htmlFor="pi-state" data-id="element-584" data-mp="fim9zbylwu2l">State</FieldLabel>
                <input id="pi-state" autoComplete="address-level1" maxLength={2} value={form.state} onChange={e => setField('state', e.target.value.toUpperCase())} className={fieldClasses} data-id="element-585" data-mp="kg5pef71jcw4" />
              </div>
            </div>
            <div data-id="element-586" data-mp="134kcire8m6rn">
              <FieldLabel htmlFor="pi-address" data-id="element-587" data-mp="1352hqdivnvbp">
                Street address{' '}
                <span className="font-medium normal-case tracking-normal text-gray-500" data-id="element-588" data-mp="pdnyc5l25s44">
                  (optional)
                </span>
              </FieldLabel>
              <input id="pi-address" autoComplete="address-line1" value={form.address} onChange={e => setField('address', e.target.value)} placeholder="123 Main St" className={fieldClasses} data-id="element-589" data-mp="1i2l2l4sfpodv" />
            </div>
          </div>}

        {step === 3 && <div className="space-y-4" data-id="element-590" data-mp="1s58it2tpxlj6">
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-591" data-mp="25ypww83my61t">
              <div data-id="element-592" data-mp="260anxnqqr6ux">
                <FieldLabel htmlFor="pi-first" data-id="element-593" data-mp="20zmvqyqs0jz4">First name</FieldLabel>
                <input id="pi-first" autoComplete="given-name" value={form.firstName} onChange={e => setField('firstName', e.target.value)} className={fieldClasses} data-id="element-594" data-mp="211pmxm3o0j5f" />
              </div>
              <div data-id="element-595" data-mp="1n5vpbz8cdg7m">
                <FieldLabel htmlFor="pi-last" data-id="element-596" data-mp="1i8vivn7kybfz">Last name</FieldLabel>
                <input id="pi-last" autoComplete="family-name" value={form.lastName} onChange={e => setField('lastName', e.target.value)} className={fieldClasses} data-id="element-597" data-mp="1i7kf3ne4x3ov" />
              </div>
              <div data-id="element-598" data-mp="31o71gmvaybzx">
                <FieldLabel htmlFor="pi-email" data-id="element-599" data-mp="31r2vw17xyijm">Email address</FieldLabel>
                <input id="pi-email" type="email" autoComplete="email" value={form.email} onChange={e => setField('email', e.target.value)} className={fieldClasses} data-id="element-600" data-mp="35oqvhhkkwzhu" />
              </div>
              <div data-id="element-601" data-mp="2i2kaluq23pa7">
                <FieldLabel htmlFor="pi-phone" data-id="element-602" data-mp="2mztz985mfwwz">Phone number</FieldLabel>
                <input id="pi-phone" type="tel" autoComplete="tel" value={form.phone} onChange={e => setField('phone', formatPhone(e.target.value))} placeholder="(555) 123-4567" className={fieldClasses} data-id="element-603" data-mp="2n03rz3i1wi6d" />
              </div>
            </div>

            {tcpaLoading ? <p className="flex items-center gap-2 text-sm text-gray-500" data-id="element-604" data-mp="246zuf71ppl6h">
                <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-605" data-mp="1wpbhxa2zthig" />
                Loading consent terms…
              </p> : tcpaError ? <p role="alert" className="text-sm text-red-600" data-id="element-606" data-mp="1mtgsnw49ko6p">
                {tcpaError}
              </p> : <label className="flex gap-3 text-xs leading-relaxed text-gray-600" data-id="element-607" data-mp="1drha2zdlpc1n">
                <input type="checkbox" checked={form.consent} onChange={e => setField('consent', e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-400 text-[#1e40af] focus:ring-[#1e40af]" data-id="element-608" data-mp="1909287rx5yql" />
                <span dangerouslySetInnerHTML={{
            __html: disclosureHtml
          }} data-id="element-609" data-mp="u2qdv6j5lnjo" />
              </label>}
          </div>}

        {error && <p role="alert" className="mt-4 text-sm font-medium text-red-600" data-id="element-610" data-mp="q1wn85ebtun3">
            {error}
          </p>}

        <div className="mt-5 flex items-center gap-3" data-id="element-611" data-mp="2dw8anmmnorvc">
          {step > 1 && <button type="button" onClick={() => {
          setError('');
          setStep(step - 1);
        }} className="flex items-center gap-1 rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50" data-id="element-612" data-mp="2iwe67dw1qyip">
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-613" data-mp="1zy1miy7ie68v" />
              Back
            </button>}
          {step < 3 ? <button type="button" onClick={handleContinue} className="flex-1 rounded-lg bg-[#e89b33] px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#17255a] transition-colors hover:bg-[#d98c22] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e40af] focus-visible:ring-offset-2" data-id="element-614" data-mp="1laypesn3lzm2">
              Continue
            </button> : <button type="submit" disabled={submitting || tcpaLoading} className="flex-1 rounded-lg bg-[#e89b33] px-6 py-3 text-sm font-bold uppercase tracking-wide text-[#17255a] transition-colors hover:bg-[#d98c22] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e40af] focus-visible:ring-offset-2" data-id="element-615" data-mp="xo4jojze2u33">
              {submitting ? 'Finding matches…' : 'See My Matches'}
            </button>}
        </div>
      </form>
    </section>;
}